import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

// Partnership Management - Partner Requests (User Management CSS)
const PartnerRequestsTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [actioningId, setActioningId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType !== 'all') params.set('partnerType', filterType);
      const res = await api.get(`/api/admin/partner-requests?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? [];
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load partner requests');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleApprove = async (id) => {
    setActioningId(id);
    try {
      await api.post(`/api/admin/partner-requests/${id}/approve`, {});
      await fetchList();
    } catch (err) {
      setError(err?.message || 'Failed to approve');
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (id) => {
    setActioningId(id);
    try {
      await api.post(`/api/admin/partner-requests/${id}/reject`, {});
      await fetchList();
    } catch (err) {
      setError(err?.message || 'Failed to reject');
    } finally {
      setActioningId(null);
    }
  };

  const filtered = search.trim()
    ? list.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(search.trim().toLowerCase()) ||
          (r.email || '').toLowerCase().includes(search.trim().toLowerCase())
      )
    : list;

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date.getTime()) ? d : date.toLocaleDateString();
  };

  const EDUCATION_PARTNER_LABELS = {
    immigration_office: 'Immigration office',
    mobility_agency: 'Mobility agency',
    language_center: 'Language center',
    training_center: 'Training center',
    educational_agency: 'Educational agency',
    student_service_office: 'Student service office',
    exam_prep_org: 'Exam-prep organization',
  };

  const displayPartnerType = (r) => {
    const match = String(r.message || '').match(/\[education_partner:([a-z_]+)\]/);
    if (match) {
      const label = EDUCATION_PARTNER_LABELS[match[1]] || match[1];
      return `Education Partner (${label})`;
    }
    const type = r.partner_type || '';
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : '—';
  };

  return (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        <select
          className="partnership-filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="school">Schools & Education Partners</option>
          <option value="teacher">Teachers</option>
          <option value="influencer">Influencers</option>
        </select>
        <select
          className="partnership-filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ marginLeft: 8 }}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: 12 }}>
          <span className="material-icons">error</span>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ padding: 24, textAlign: 'center' }}>Loading...</p>
      ) : (
        <table className="users-table">
          <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Partner Type</th>
            <th>Organization</th>
            <th>Audience Size</th>
            <th>Business Doc</th>
            <th>Status</th>
            <th>Date Submitted</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 24 }}>
                  No partner requests found.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.name || '—'}</td>
                  <td>{r.email || '—'}</td>
                  <td>{displayPartnerType(r)}</td>
                  <td>{r.school_or_social || '—'}</td>
                  <td>{r.audience_size || '—'}</td>
                  <td>
                    {r.business_doc_url ? (
                      <a href={r.business_doc_url} target="_blank" rel="noreferrer">
                        View
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${r.status}`}>{r.status || '—'}</span>
                  </td>
                  <td>{formatDate(r.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      {r.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            className="action-btn toggle"
                            title="Approve"
                            disabled={actioningId === r.id}
                            onClick={() => handleApprove(r.id)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="action-btn delete"
                            title="Reject"
                            disabled={actioningId === r.id}
                            onClick={() => handleReject(r.id)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PartnerRequestsTab;
